import { useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@/components/ui/select';
import { Calendar, Clock, Music, Users, Ticket, ArrowRight, Sparkles, Moon, Sun, Star, CheckCircle, ExternalLink, Send, Briefcase } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import LaVillaLogo from '@/components/LaVillaLogo';

// Event configuration - easily editable by admin
const eventConfig = {
  francamente: {
    id: 'francamente',
    name: 'FRANCAMENTE',
    tagline: 'Il martedì che accende la settimana.',
    schedule: 'Ogni Martedì',
    badge: 'Weekly',
    description: 'Aperitivo che diventa sera. Piscina, cocktail signature, musica selezionata. L\'energia cresce con il tramonto.',
    mood: 'sunset, piscina, aperitivo, energia crescente',
    image: '/images/event-francamente.jpg',
    status: 'active', // active | tba | soldout
    ticketUrl: 'https://www.ticketsms.it/event/Francamente',
  },
  partyvillage: {
    id: 'partyvillage',
    name: 'PARTY VILLA\'GE',
    tagline: 'La notte ha un nuovo indirizzo.',
    schedule: 'Weekend',
    badge: 'Weekend',
    description: 'Format variabili, capienza controllata. Ogni weekend un\'esperienza diversa. Club vibe, eleganza naturale.',
    mood: 'notte elegante, club vibe, format variabili',
    image: '/images/event-partyvillage.jpg',
    status: 'active',
    ticketUrl: 'https://www.ticketsms.it/promoter/luigi.sereni26',
  },
  afterfranco: {
    id: 'afterfranco',
    name: 'AFTERFRANCO',
    tagline: 'Il giorno dopo, fatto bene.',
    schedule: '2 Domeniche al mese',
    badge: 'Twice Monthly',
    description: 'Pool party diurno, sound selezionato, atmosfera rilassata. Il modo migliore per chiudere il weekend.',
    mood: 'day-after, pool, relax, sound selezionato',
    image: '/images/event-afterfranco.png',
    status: 'active',
    ticketUrl: 'https://www.ticketsms.it/promoter/luigi.sereni26',
  },
  guestedition: {
    id: 'guestedition',
    name: 'GUEST EDITION',
    tagline: 'Artisti selezionati. Serate uniche.',
    schedule: 'Selected Saturdays',
    badge: 'Selected',
    description: 'Ospiti speciali, produzioni esclusive. Capienza controllata, esperienza amplificata. Annunci a sorpresa.',
    mood: 'artisti selezionati, capienza controllata',
    image: '/images/guest-edition-artist.jpg',
    status: 'tba',
    ticketUrl: '',
  },
  specialchapters: {
    id: 'specialchapters',
    name: 'SPECIAL CHAPTERS',
    tagline: 'Quando l\'ordinario non basta.',
    schedule: 'Eventi Speciali',
    badge: 'Special',
    description: 'Collaborazioni, one-off, sorprese. Capitoli unici della storia de La Villa. Da vivere, da ricordare.',
    mood: 'collaborazioni, one-off, sorprese',
    image: '/images/special-chapters-concert.jpg',
    status: 'tba',
    ticketUrl: '',
  },
};

const scheduleItems = [
  {
    day: 'Ogni Martedì',
    event: 'FRANCAMENTE',
    tagline: 'Aperitivo che diventa sera. Sound, piscina, cocktail signature.',
    badge: 'Weekly',
  },
  {
    day: 'Weekend',
    event: 'PARTY VILLA\'GE',
    tagline: 'Format variabili. Club vibe. Eleganza naturale.',
    badge: 'Weekend',
  },
  {
    day: '2 Domeniche al mese',
    event: 'AFTERFRANCO',
    tagline: 'Pool party diurno. Il giorno dopo, fatto bene.',
    badge: 'Twice Monthly',
  },
  {
    day: 'Date Selezionate',
    event: 'GUEST & SPECIAL',
    tagline: 'Artisti, collaborazioni, sorprese. Soon.',
    badge: 'TBA',
  },
];

const rules = [
  { icon: Users, text: 'Capienza controllata — esperienza garantita' },
  { icon: Ticket, text: 'Ticket nominativi — accesso su prenotazione' },
  { icon: Clock, text: 'Orari comunicati nel ticket di conferma' },
  { icon: Sparkles, text: 'Dress code consigliato — elegante, estivo' },
  { icon: CheckCircle, text: 'Pagamenti: carta, contactless, app' },
  { icon: Calendar, text: 'Policy meteo: comunicazioni tempestive via email' },
];

// ============== CALENDARIO STAGIONALE CONFIG ==============
// Admin-friendly structure - modify events here
const calendarConfig = {
  giugno: {
    month: 'Giugno',
    year: 2026,
    events: [
      { day: 6, code: 'PV', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Party-Village-Castelfranco-Emilia-La-Villa-06-06-2026', description: 'PARTY VILLAGE w/ Il Rosso & Ipork' },
      { day: 9, code: 'FR', status: 'active', ticketUrl: '', description: 'FRANCAMENTE' },
      { day: 14, code: 'AF', status: 'active', ticketUrl: '', description: 'AFTERFRANCO' },
      { day: 16, code: 'FR', status: 'active', ticketUrl: '', description: 'FRANCAMENTE' },
      { day: 20, code: 'AU', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Aura-No-Labes-La-Villa-Castelfranco-Emilia-La-Villa-20-06-2026', description: 'AURANOLABELS' },
      { day: 23, code: 'FR', status: 'active', ticketUrl: '', description: 'FRANCAMENTE' },
      { day: 26, code: 'BO', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Bonita-Castelfranco-Emilia-La-Villa-26-06-2026', description: 'BONITA' },
      { day: 27, code: 'DE', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Disco-Euphoria-Castelfranco-Emilia-La-Villa-27-06-2026', description: 'DISCO EUPHORIA' },
      { day: 28, code: 'AF', status: 'active', ticketUrl: '', description: 'AFTERFRANCO' },
      { day: 30, code: 'FR', status: 'active', ticketUrl: '', description: 'FRANCAMENTE' },
    ],
  },
  luglio: {
    month: 'Luglio',
    year: 2026,
    events: [
      { day: 4, code: 'PV', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Party-Village-Castelfranco-Emilia-La-Villa2-04-07-2026', description: 'PARTY VILLAGE w/ 22Simba' },
      { day: 7, code: 'FR', status: 'active', ticketUrl: '', description: 'FRANCAMENTE' },
      { day: 18, code: 'OB', status: 'active', ticketUrl: '', description: 'OBSCENE' },
      { day: 19, code: 'AF', status: 'active', ticketUrl: '', description: 'AFTERFRANCO' },
      { day: 25, code: 'PV', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Party-Village-Castelfranco-Emilia-La-Villa2-25-07-2026', description: 'PARTY VILLAGE w/ Artie5' },
    ],
  },
  agosto: {
    month: 'Agosto',
    year: 2026,
    events: [
      { day: 1, code: 'PV', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Party-Village-Castelfranco-Emilia-La-Villa-01-08-2026', description: 'PARTY VILLAGE w/ IvanBi & G.minero' },
      { day: 15, code: 'DE', status: 'active', ticketUrl: '', description: 'DISCO EUPHORIA' },
      { day: 22, code: 'PV', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Party-Village-Castelfranco-Emilia-La-Villa-22-08-2026', description: 'PARTY VILLAGE w/ Lil Cr' },
      { day: 28, code: 'S54', status: 'active', ticketUrl: '', description: 'A NIGHT IN STUDIO 54' },
      { day: 29, code: 'DF', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Different-Castelfranco-Emilia-La-Villa-29-08-2026', description: 'DIFFERENT' },
      { day: 30, code: 'AF', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Afterfranco-Castelfranco-Emilia-La-Villa-30-08-2026', description: 'AFTERFRANCO' },
    ],
  },
  settembre: {
    month: 'Settembre',
    year: 2026,
    events: [
      { day: 1, code: 'FR', status: 'active', ticketUrl: '', description: 'FRANCAMENTE' },
      { day: 5, code: 'PV', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Party-Village-Castelfranco-Emilia-La-Villa2-05-09-2026', description: 'PARTY VILLAGE w/ Ipork' },
      { day: 8, code: 'FR', status: 'active', ticketUrl: '', description: 'FRANCAMENTE' },
      { day: 12, code: 'PV', status: 'active', ticketUrl: 'https://www.ticketsms.it/event/Party-Village-Castelfranco-Emilia-La-Villa2-12-09-2026', description: 'PARTY VILLAGE w/ Bello Figo & Isabo' },
      { day: 13, code: 'AF', status: 'active', ticketUrl: '', description: 'AFTERFRANCO' },
      { day: 15, code: 'FR', status: 'active', ticketUrl: '', description: 'FRANCAMENTE' },
      { day: 19, code: 'DF', status: 'active', ticketUrl: '', description: 'DIFFERENT' },
    ],
  },
};

// Ghost words for easter eggs
const ghostWords = ['Chapter', 'Edition', 'Guest', 'Pool', 'After', 'Sunset', 'Night', 'Sound', 'Wave', 'Villa'];
const ghostNumbers = ['03', '07', '12', '17', '21', '24', '29', '05', '14', '19', '08', '26'];

// Calendar Day Cell Component
const CalendarDayCell = ({ day, event, monthDays, firstDayOfWeek }) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasEvent = event !== null;
  const isHidden = event?.status === 'hidden';
  const isActive = event?.status === 'active';
  const isSoldOut = event?.status === 'soldout';
  
  // Random ghost elements
  const showGhost = !hasEvent && Math.random() > 0.7;
  const ghostWord = ghostWords[Math.floor(Math.random() * ghostWords.length)];
  const ghostNum = ghostNumbers[Math.floor(Math.random() * ghostNumbers.length)];
  
  const hasTicket = isActive && event?.ticketUrl;
  
  if (day === null) {
    return <div className="aspect-square" />;
  }
  
  const cellContent = (
    <motion.div
      className={`relative aspect-square border border-stone-200/50 flex items-center justify-center transition-all duration-300 ${
        hasEvent ? 'bg-champagne/5' : 'bg-white'
      } ${isHovered && hasEvent ? 'border-champagne/50' : ''} ${hasTicket ? 'cursor-pointer hover:bg-champagne/10' : 'cursor-default'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: hasEvent ? 1.02 : 1 }}
    >
      {/* Day number */}
      <span className={`absolute top-1 left-2 text-[10px] font-body ${hasEvent ? 'text-champagne' : 'text-stone-400'}`}>
        {day}
      </span>
      
      {/* Ghost elements (easter eggs) */}
      {showGhost && (
        <>
          <span className="absolute inset-0 flex items-center justify-center text-[8px] text-stone-300/20 blur-[2px] select-none pointer-events-none">
            {ghostWord}
          </span>
          <span className="absolute bottom-1 right-1 text-[10px] text-stone-300/15 blur-[1px] select-none pointer-events-none">
            {ghostNum}
          </span>
        </>
      )}
      
      {/* Event content */}
      {hasEvent && (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-1">
          {/* Frosted glass overlay for hidden events */}
          {isHidden && (
            <div className="absolute inset-0 backdrop-blur-[6px] bg-white/40 z-10" />
          )}
          
          {/* Event indicator dot */}
          <div className={`w-2 h-2 rounded-full mb-1 ${
            isActive ? 'bg-green-500' : isSoldOut ? 'bg-red-500' : 'bg-champagne/60'
          } ${isHidden ? 'blur-[2px]' : ''}`} />
          
          {/* Event code - blurred if hidden */}
          <span className={`text-[7px] font-mono tracking-wider text-center leading-tight px-0.5 ${
            isHidden ? 'blur-[3px] text-stone-400' : 'text-champagne'
          }`}>
            {isHidden ? '███' : (event.description || event.code)}
          </span>
          
          {/* TBA badge for hidden */}
          {isHidden && (
            <span className="absolute bottom-1 text-[6px] uppercase tracking-widest text-stone-500 z-20">
              TBA
            </span>
          )}
          
          {/* Active badge */}
          {isActive && (
            <span className="absolute bottom-1 text-[6px] uppercase tracking-widest text-green-600 font-semibold">
              LIVE
            </span>
          )}
          
          {/* Sold out badge */}
          {isSoldOut && (
            <span className="absolute bottom-1 text-[6px] uppercase tracking-widest text-red-500">
              SOLD
            </span>
          )}
        </div>
      )}
      
      {/* Hover tooltip */}
      {isHovered && hasEvent && isHidden && (
        <motion.div
          className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-night text-ivory text-[9px] uppercase tracking-wider whitespace-nowrap z-30 shadow-lg"
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
        >
          To Be Announced
        </motion.div>
      )}
    </motion.div>
  );

  if (hasTicket) {
    return (
      <a href={event.ticketUrl} target="_blank" rel="noopener noreferrer">
        {cellContent}
      </a>
    );
  }

  return cellContent;
};

// Month Calendar Component
const MonthCalendar = ({ monthData }) => {
  const daysOfWeek = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  
  // Calculate days in month and first day
  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (month, year) => {
    const day = new Date(year, month - 1, 1).getDay();
    return day === 0 ? 6 : day - 1; // Monday = 0
  };
  
  const monthIndex = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'].indexOf(monthData.month) + 1;
  const daysInMonth = getDaysInMonth(monthIndex, monthData.year);
  const firstDay = getFirstDayOfMonth(monthIndex, monthData.year);
  
  // Create calendar grid
  const calendarDays = [];
  
  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  
  // Days of month
  for (let day = 1; day <= daysInMonth; day++) {
    const event = monthData.events.find(e => e.day === day) || null;
    calendarDays.push({ day, event });
  }
  
  return (
    <motion.div
      className="bg-white border border-stone-200/80 shadow-sm p-6 relative overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      {/* Decorative ghost numbers in background */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
        {[...Array(5)].map((_, i) => (
          <span
            key={`ghost-${i}`}
            className="absolute text-6xl font-heading text-stone-100/40 blur-[1px]"
            style={{
              top: `${Math.random() * 80}%`,
              left: `${Math.random() * 80}%`,
              transform: `rotate(${Math.random() * 30 - 15}deg)`,
            }}
          >
            {ghostNumbers[Math.floor(Math.random() * ghostNumbers.length)]}
          </span>
        ))}
      </div>
      
      {/* Month header */}
      <h3 className="font-heading text-3xl text-night mb-6 relative z-10">
        {monthData.month}
        <span className="text-sm font-body text-stone-400 ml-2">{monthData.year}</span>
      </h3>
      
      {/* Days of week header */}
      <div className="grid grid-cols-7 gap-1 mb-2 relative z-10">
        {daysOfWeek.map(day => (
          <div key={day} className="text-center text-[10px] uppercase tracking-widest text-stone-400 font-body py-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 relative z-10">
        {calendarDays.map((dayData, index) => (
          <CalendarDayCell
            key={`cell-${index}`}
            day={dayData?.day || null}
            event={dayData?.event || null}
          />
        ))}
      </div>
      
      {/* Bottom decoration */}
      <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between relative z-10">
        <span className="text-[10px] uppercase tracking-widest text-stone-400">
          {monthData.events.length} eventi programmati
        </span>
        <span className="text-[10px] text-stone-300 blur-[2px] select-none">
          ████ ████
        </span>
      </div>
    </motion.div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    tba: 'bg-champagne/20 text-champagne border-champagne/30',
    soldout: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  const labels = {
    active: 'Ticket Disponibili',
    tba: 'TBA',
    soldout: 'Sold Out',
  };
  return (
    <span className={`px-3 py-1 text-xs uppercase tracking-wider border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

// Event Card Component
const EventCard = ({ event }) => {
  const handleTicketClick = () => {
    if (event.status === 'active' && event.ticketUrl) {
      window.open(event.ticketUrl, '_blank');
    }
  };

  return (
    <motion.div
      className="group relative bg-stone-900/50 border border-stone-800 overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      data-testid={`event-card-${event.id}`}
    >
      {/* Image */}
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={event.image}
          alt={event.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-night via-night/50 to-transparent" />
      </div>
      
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="px-2 py-1 text-[10px] uppercase tracking-widest bg-champagne/20 text-champagne border border-champagne/30">
            {event.badge}
          </span>
          <StatusBadge status={event.status} />
        </div>
        
        <h3 className="font-heading text-2xl text-ivory mb-1">{event.name}</h3>
        <p className="font-accent text-sm italic text-champagne mb-3">{event.tagline}</p>
        <p className="font-body text-xs text-stone-400 mb-4 line-clamp-2">{event.description}</p>
        
        <div className="flex gap-3">
          {event.status === 'active' ? (
            <>
              <Button
                onClick={handleTicketClick}
                className="flex-1 bg-champagne text-night hover:bg-ivory text-xs uppercase tracking-widest"
                data-testid={`ticket-btn-${event.id}`}
              >
                <Ticket size={14} className="mr-2" />
                Biglietti
              </Button>
              <Button
                variant="outline"
                className="border-stone-600 text-stone-300 hover:bg-stone-800 text-xs uppercase tracking-widest"
              >
                Info
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              className="flex-1 border-champagne/50 text-champagne hover:bg-champagne/10 text-xs uppercase tracking-widest"
              onClick={() => document.getElementById('priority-list')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Lista Prioritaria
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default function NightEvents() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    preferences: [],
  });
  const [submitting, setSubmitting] = useState(false);

  // Work With Us form state
  const [workFormData, setWorkFormData] = useState({
    name: '',
    contact: '',
    role: '',
  });
  const [workSubmitting, setWorkSubmitting] = useState(false);

  const workRoles = [
    'PR',
    'Barman',
    'Cameriere',
    'Bagnino',
    'Ragazza Immagine',
    'Altro'
  ];

  const handlePreferenceChange = (eventId, checked) => {
    setFormData(prev => ({
      ...prev,
      preferences: checked
        ? [...prev.preferences, eventId]
        : prev.preferences.filter(id => id !== eventId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.contact) {
      toast.error('Compila tutti i campi');
      return;
    }
    setSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/priority-list/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          contact: formData.contact,
          preferences: formData.preferences
        })
      });
      
      if (response.ok) {
        toast.success('Iscrizione completata! Ti contatteremo presto.');
        setFormData({ name: '', contact: '', preferences: [] });
      } else {
        toast.error('Errore durante l\'iscrizione. Riprova.');
      }
    } catch (error) {
      toast.error('Errore di connessione. Riprova.');
    }
    
    setSubmitting(false);
  };

  const handleWorkSubmit = async (e) => {
    e.preventDefault();
    if (!workFormData.name || !workFormData.contact || !workFormData.role) {
      toast.error('Compila tutti i campi');
      return;
    }
    setWorkSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/work-with-us/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: workFormData.name,
          contact: workFormData.contact,
          role: workFormData.role
        })
      });
      
      if (response.ok) {
        toast.success('Candidatura inviata! Ti contatteremo presto.');
        setWorkFormData({ name: '', contact: '', role: '' });
      } else {
        toast.error('Errore durante l\'invio. Riprova.');
      }
    } catch (error) {
      toast.error('Errore di connessione. Riprova.');
    }
    
    setWorkSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>Night Events | La Villa - Castelfranco Emilia</title>
        <meta name="description" content="Gli eventi serali de La Villa. Francamente, Party Villa'ge, AfterFranco e Guest Edition. Capienza controllata, esperienza premium." />
        <meta property="og:title" content="Night Events | La Villa" />
        <meta property="og:description" content="Un ritmo settimanale. Esperienze selezionate. Capienza controllata." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-night min-h-screen text-ivory" data-testid="night-events-page">
        
        {/* 1. HERO FULL SCREEN */}
        <section ref={heroRef} className="relative h-[70vh] flex items-center justify-center overflow-hidden">
          <motion.div className="absolute inset-0" style={{ y: heroY }}>
            <img
              src="/images/pool-night-1.jpeg"
              alt="La Villa Night"
              className="w-full h-full object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-night/40 via-night/20 to-night" />
            <div className="absolute inset-0 bg-amber-900/10" /> {/* Warm overlay */}
          </motion.div>
          
          <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
            <motion.p
              className="font-body text-xs uppercase tracking-[0.4em] text-champagne mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              La Villa Night
            </motion.p>
            <motion.h1
              className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-7xl text-ivory tracking-tight mb-6 flex items-center justify-center gap-2 md:gap-3"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Gli Eventi de <LaVillaLogo size="5xl" className="h-10 sm:h-14 md:h-28 lg:h-44 mt-1 md:mt-8" dark={false} />
            </motion.h1>
            <motion.p
              className="font-body text-lg md:text-xl text-stone-300 mb-4 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              Un ritmo settimanale. Esperienze selezionate. Capienza controllata.
            </motion.p>
            <motion.p
              className="font-body text-sm text-stone-500 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              Le date vengono rilasciate presto. I tickets saranno aggiornati.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <Button
                className="bg-champagne text-night hover:bg-ivory px-8 py-6 text-sm uppercase tracking-widest"
                onClick={() => document.getElementById('tickets')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-tickets-btn"
              >
                <Ticket size={16} className="mr-2" />
                Acquista Ticket
              </Button>
              <Button
                variant="outline"
                className="border-ivory/30 text-ivory hover:bg-ivory/10 px-8 py-6 text-sm uppercase tracking-widest"
                onClick={() => document.getElementById('priority-list')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Lista Prioritaria
              </Button>
            </motion.div>
          </div>
        </section>

        {/* 2. IL RITMO DELLA STAGIONE */}
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-stone-800">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">
                Il Calendario
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight">
                Il Ritmo della Stagione
              </h2>
            </motion.div>

            <div className="space-y-6">
              {scheduleItems.map((item, index) => (
                <motion.div
                  key={item.event}
                  className="group flex flex-col md:flex-row md:items-center gap-4 md:gap-8 p-6 md:p-8 border border-stone-800 hover:border-champagne/30 transition-colors"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`schedule-item-${index}`}
                >
                  <div className="md:w-48 flex-shrink-0">
                    <span className="px-3 py-1 text-[10px] uppercase tracking-widest bg-stone-800 text-stone-400 border border-stone-700">
                      {item.badge}
                    </span>
                    <p className="font-body text-sm text-champagne mt-2">{item.day}</p>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-heading text-2xl md:text-3xl text-ivory group-hover:text-champagne transition-colors">
                      {item.event}
                    </h3>
                    <p className="font-body text-sm text-stone-400 mt-1">{item.tagline}</p>
                  </div>
                  
                  <ArrowRight className="hidden md:block text-stone-600 group-hover:text-champagne group-hover:translate-x-2 transition-all" size={24} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. ACQUISTA I TICKET */}
        <section id="tickets" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-stone-900/30 border-t border-stone-800">
          <div className="max-w-[1800px] mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">
                Hub Operativo
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight mb-4">
                Acquista i Ticket
              </h2>
              <p className="font-body text-stone-400 max-w-xl mx-auto">
                Seleziona la tua data. Capienza limitata, prenotazione consigliata, con accesso prioritario.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.values(eventConfig).map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        </section>

        {/* CALENDARIO STAGIONALE */}
        <section id="calendario" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-ivory border-t border-stone-200">
          <div className="max-w-[1600px] mx-auto">
            {/* Section Header */}
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">
                Maggio — Settembre 2026
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight mb-4">
                Calendario Stagionale
              </h2>
              <p className="font-body text-stone-500 max-w-xl mx-auto mb-2">
                Programmazione completa. Rilascio progressivo.
              </p>
              <p className="font-body text-sm text-stone-400 max-w-2xl mx-auto">
                Le date e le line up vengono pubblicate gradualmente. I ticket attivi sono sempre disponibili nei link ufficiali.
              </p>
            </motion.div>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Button
                variant="outline"
                className="border-champagne text-champagne hover:bg-champagne hover:text-night px-6 py-5 text-sm uppercase tracking-widest"
                onClick={() => document.getElementById('priority-list')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Send size={14} className="mr-2" />
                Ricevi le date prima degli altri
              </Button>
              <Button
                className="bg-night text-ivory hover:bg-stone-800 px-6 py-5 text-sm uppercase tracking-widest"
                onClick={() => document.getElementById('tickets')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Ticket size={14} className="mr-2" />
                Vedi i ticket attivi
              </Button>
            </motion.div>

            {/* Calendar Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {Object.values(calendarConfig).map((monthData, index) => (
                <motion.div
                  key={monthData.month}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <MonthCalendar monthData={monthData} />
                </motion.div>
              ))}
            </div>

            {/* Legend */}
            <motion.div
              className="flex flex-wrap justify-center gap-6 mb-12 p-4 bg-white border border-stone-200"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-champagne/60 blur-[1px]" />
                <span className="text-xs text-stone-500">TBA</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-xs text-stone-500">Ticket Attivi</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-xs text-stone-500">Sold Out</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-400 blur-[2px]">███</span>
                <span className="text-xs text-stone-500">Info Nascosta</span>
              </div>
            </motion.div>

            {/* Bottom text */}
            <motion.div
              className="text-center max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h3 className="font-heading text-2xl text-night mb-3">
                Rilascio Programmazione
              </h3>
              <p className="font-body text-sm text-stone-500 mb-6">
                Il calendario completo è già definito. La pubblicazione avviene per capitoli, per proteggere la qualità e la capienza.
              </p>
              <p className="font-accent text-xl italic text-champagne">
                "Ogni gesto, una firma."
              </p>
            </motion.div>
          </div>
        </section>

        {/* 4. COME FUNZIONA */}
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-stone-800">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">
                Le Regole
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight">
                Come Funziona
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {rules.map((rule, ruleIdx) => (
                <motion.div
                  key={rule.text}
                  className="flex items-start gap-4 p-5 border border-stone-800"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: ruleIdx * 0.1 }}
                >
                  <rule.icon className="text-champagne flex-shrink-0 mt-1" size={20} />
                  <p className="font-body text-sm text-stone-300">{rule.text}</p>
                </motion.div>
              ))}
            </div>

            <motion.p
              className="text-center mt-12 font-accent text-xl italic text-champagne"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              "Ogni gesto, una firma."
            </motion.p>
          </div>
        </section>

        {/* 5. LISTA PRIORITARIA */}
        <section id="priority-list" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-stone-900/50 to-night border-t border-stone-800">
          <div className="max-w-2xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">
                Accesso Anticipato
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight mb-4">
                Lista Prioritaria
              </h2>
              <p className="font-body text-stone-400">
                Ricevi le date prima degli altri. Annunci selezionati. Nessuno spam.
              </p>
            </motion.div>

            <motion.form
              onSubmit={handleSubmit}
              className="space-y-6 p-8 border border-stone-800 bg-stone-900/30"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="priority-form"
            >
              <div>
                <Label htmlFor="name" className="text-stone-300 text-sm">Nome</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Il tuo nome"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-2 bg-stone-800/50 border-stone-700 text-ivory placeholder:text-stone-500 focus:border-champagne"
                  data-testid="priority-name"
                />
              </div>

              <div>
                <Label htmlFor="contact" className="text-stone-300 text-sm">Email o WhatsApp</Label>
                <Input
                  id="contact"
                  type="text"
                  placeholder="email@esempio.com o +39..."
                  value={formData.contact}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact: e.target.value }))}
                  className="mt-2 bg-stone-800/50 border-stone-700 text-ivory placeholder:text-stone-500 focus:border-champagne"
                  data-testid="priority-contact"
                />
              </div>

              <div>
                <Label className="text-stone-300 text-sm mb-3 block">Preferenze (opzionale)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {['Francamente', 'Party Villa\'ge', 'AfterFranco', 'Guest Edition', 'Special'].map((pref) => (
                    <div key={pref} className="flex items-center space-x-2">
                      <Checkbox
                        id={pref}
                        checked={formData.preferences.includes(pref)}
                        onCheckedChange={(checked) => handlePreferenceChange(pref, checked)}
                        className="border-stone-600 data-[state=checked]:bg-champagne data-[state=checked]:border-champagne"
                      />
                      <Label htmlFor={pref} className="text-sm text-stone-400 cursor-pointer">
                        {pref}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-champagne text-night hover:bg-ivory py-6 text-sm uppercase tracking-widest"
                data-testid="priority-submit"
              >
                {submitting ? (
                  'Invio in corso...'
                ) : (
                  <>
                    <Send size={16} className="mr-2" />
                    Iscriviti alla Lista
                  </>
                )}
              </Button>
            </motion.form>
          </div>
        </section>

        {/* 6. LAVORA CON NOI */}
        <section id="work-with-us" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-night border-t border-stone-800">
          <div className="max-w-2xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">
                Entra nel Team
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight mb-4">
                Lavora con Noi
              </h2>
              <p className="font-body text-stone-400">
                PR, Barman, Cameriere, Bagnino, Ragazza Immagine?<br/>
                Lasciaci un tuo recapito.
              </p>
            </motion.div>

            <motion.form
              onSubmit={handleWorkSubmit}
              className="space-y-6 p-8 border border-champagne/30 bg-gradient-to-b from-stone-900/50 to-stone-900/80"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="work-form"
            >
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-700">
                <Briefcase className="text-champagne" size={24} />
                <span className="font-heading text-xl text-ivory">Candidatura</span>
              </div>

              <div>
                <Label htmlFor="work-name" className="text-stone-300 text-sm">Nome e Cognome</Label>
                <Input
                  id="work-name"
                  type="text"
                  placeholder="Il tuo nome completo"
                  value={workFormData.name}
                  onChange={(e) => setWorkFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-2 bg-stone-800/50 border-stone-700 text-ivory placeholder:text-stone-500 focus:border-champagne"
                  data-testid="work-name"
                />
              </div>

              <div>
                <Label htmlFor="work-contact" className="text-stone-300 text-sm">Email o WhatsApp</Label>
                <Input
                  id="work-contact"
                  type="text"
                  placeholder="email@esempio.com o +39..."
                  value={workFormData.contact}
                  onChange={(e) => setWorkFormData(prev => ({ ...prev, contact: e.target.value }))}
                  className="mt-2 bg-stone-800/50 border-stone-700 text-ivory placeholder:text-stone-500 focus:border-champagne"
                  data-testid="work-contact"
                />
              </div>

              <div>
                <Label htmlFor="work-role" className="text-stone-300 text-sm">Ruolo Desiderato</Label>
                <Select 
                  value={workFormData.role} 
                  onValueChange={(value) => setWorkFormData(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger 
                    className="mt-2 bg-stone-800/50 border-stone-700 text-ivory focus:border-champagne"
                    data-testid="work-role"
                  >
                    <SelectValue placeholder="Seleziona un ruolo" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {workRoles.map((role) => (
                      <SelectItem 
                        key={role} 
                        value={role}
                        className="text-ivory hover:bg-champagne/20 focus:bg-champagne/20"
                      >
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={workSubmitting}
                className="w-full bg-champagne text-night hover:bg-ivory py-6 text-sm uppercase tracking-widest mt-4"
                data-testid="work-submit"
              >
                {workSubmitting ? (
                  'Invio in corso...'
                ) : (
                  <>
                    <Briefcase size={16} className="mr-2" />
                    Invia Candidatura
                  </>
                )}
              </Button>

              <p className="text-center text-stone-500 text-xs mt-4">
                Ti contatteremo entro 48 ore.
              </p>
            </motion.form>
          </div>
        </section>

        {/* MOBILE STICKY CTA */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-night/95 backdrop-blur-lg border-t border-stone-800 md:hidden z-50">
          <div className="flex gap-3">
            <Button
              className="flex-1 bg-champagne text-night hover:bg-ivory text-xs uppercase tracking-widest"
              onClick={() => document.getElementById('tickets')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <Ticket size={14} className="mr-1" />
              Biglietti
            </Button>
            <Button
              variant="outline"
              className="flex-1 border-champagne/50 text-champagne text-xs uppercase tracking-widest"
              onClick={() => document.getElementById('priority-list')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Lista
            </Button>
          </div>
        </div>

        {/* Spacer for mobile sticky */}
        <div className="h-20 md:hidden" />
      </div>
    </>
  );
}
